/**
 * Utility functions for Menu Configuration
 */

/**
 * Convert a menu config array (with id, visible, order) into the routes format
 * consumed by NavbarVerticalMenu. Filters out invisible items and sorts by order.
 */
export function menuConfigToRoutes(menuConfig) {
  if (!menuConfig || !Array.isArray(menuConfig)) return [];

  return menuConfig
    .filter((item) => item.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((item) => {
      const route = {
        name: item.name,
        to: item.to,
      };
      if (item.icon) route.icon = item.icon;
      if (item.exact) route.exact = true;
      if (item.border) route.border = true;
      if (item.badge) route.badge = item.badge;
      if (item.isCustomGroup) route.isCustomGroup = true;
      if (item.children && item.children.length > 0) {
        const visibleChildren = menuConfigToRoutes(item.children);
        if (visibleChildren.length > 0) {
          route.children = visibleChildren;
        }
      }
      return route;
    });
}

/**
 * Merge a saved config over the master list.
 * - Preserves visibility/order/name/icon overrides from the saved config.
 * - Any new master items not in the saved config are appended as visible at the end.
 * - Any saved items whose IDs no longer exist in the master list are dropped.
 */
export function mergeMenuConfig(masterList, savedConfig) {
  if (!savedConfig || !Array.isArray(savedConfig) || savedConfig.length === 0) {
    return masterList;
  }
  if (!masterList || !Array.isArray(masterList)) {
    return savedConfig;
  }

  const savedMap = new Map();
  savedConfig.forEach((item) => savedMap.set(item.id, item));

  const merged = [];
  const usedIds = new Set();

  // First, apply saved order and overrides for items that exist in master
  savedConfig.forEach((savedItem) => {
    const masterItem = masterList.find((m) => m.id === savedItem.id);
    if (masterItem) {
      usedIds.add(savedItem.id);
      merged.push({
        ...masterItem,
        name: savedItem.name || masterItem.name,
        icon: savedItem.icon !== undefined ? savedItem.icon : masterItem.icon,
        visible: savedItem.visible !== undefined ? savedItem.visible : true,
        order: savedItem.order !== undefined ? savedItem.order : merged.length,
        children: mergeChildMenuConfig(
          masterItem.children,
          savedItem.children
        ),
      });
    } else if (savedItem.isCustomGroup || (typeof savedItem.id === 'string' && savedItem.id.startsWith('custom-'))) {
      // Custom groups and custom child routes have no master entry — preserve them as-is
      usedIds.add(savedItem.id);
      merged.push({
        ...savedItem,
        order: savedItem.order !== undefined ? savedItem.order : merged.length,
        children: savedItem.children ? mergeChildMenuConfig(undefined, savedItem.children) : savedItem.children,
      });
    }
  });

  // Then, append any new master items not in saved config
  masterList.forEach((masterItem) => {
    if (!usedIds.has(masterItem.id)) {
      merged.push({
        ...masterItem,
        visible: true,
        order: merged.length,
      });
    }
  });

  return merged;
}

/**
 * Merge children specifically - handles the recursive case.
 */
function mergeChildMenuConfig(masterChildren, savedChildren) {
  if (!savedChildren || savedChildren.length === 0) {
    return (masterChildren && masterChildren.length > 0) ? masterChildren : undefined;
  }
  if (!masterChildren || masterChildren.length === 0) {
    // No master children (e.g. custom group) — preserve all saved children as-is
    return savedChildren.map((item, idx) => ({ ...item, order: item.order !== undefined ? item.order : idx }));
  }

  const savedMap = new Map();
  savedChildren.forEach((item) => savedMap.set(item.id, item));

  const merged = [];
  const usedIds = new Set();

  // Apply saved order first
  savedChildren.forEach((savedItem) => {
    const masterItem = masterChildren.find((m) => m.id === savedItem.id);
    if (masterItem) {
      usedIds.add(savedItem.id);
      merged.push({
        ...masterItem,
        name: savedItem.name || masterItem.name,
        icon: savedItem.icon !== undefined ? savedItem.icon : masterItem.icon,
        visible: savedItem.visible !== undefined ? savedItem.visible : true,
        order: savedItem.order !== undefined ? savedItem.order : merged.length,
        children: mergeChildMenuConfig(
          masterItem.children,
          savedItem.children
        ),
      });
    } else if (savedItem.isCustomGroup || (typeof savedItem.id === 'string' && savedItem.id.startsWith('custom-'))) {
      // Custom child routes have no master entry — preserve them as-is
      usedIds.add(savedItem.id);
      merged.push({
        ...savedItem,
        order: savedItem.order !== undefined ? savedItem.order : merged.length,
      });
    }
  });

  // Append new master items
  masterChildren.forEach((masterItem) => {
    if (!usedIds.has(masterItem.id)) {
      merged.push({
        ...masterItem,
        visible: true,
        order: merged.length,
      });
    }
  });

  return merged.length > 0 ? merged : undefined;
}

/**
 * Deep clone a menu config array.
 */
export function cloneMenuConfig(config) {
  return JSON.parse(JSON.stringify(config));
}

/**
 * Update a specific item in the config tree by its ID.
 * Returns a new array (immutable update).
 */
export function updateMenuItem(config, itemId, updates) {
  return config.map((item) => {
    if (item.id === itemId) {
      return { ...item, ...updates };
    }
    if (item.children) {
      return {
        ...item,
        children: updateMenuItem(item.children, itemId, updates),
      };
    }
    return item;
  });
}

/**
 * Reorder items at a given level. Used by drag-and-drop.
 * Returns a new array with updated order values.
 */
export function reorderItems(items, fromIndex, toIndex) {
  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result.map((item, idx) => ({ ...item, order: idx }));
}

/**
 * Count visible items in a config tree.
 */
export function countVisibleItems(config) {
  if (!config || !Array.isArray(config)) return 0;
  return config.reduce((count, item) => {
    if (item.visible === false) return count;
    const childCount = item.children ? countVisibleItems(item.children) : 0;
    return count + 1 + childCount;
  }, 0);
}

/**
 * Create a new custom group (parent menu with no route).
 */
export function createGroup(name, icon) {
  const id = "custom-group-" + Date.now();
  const group = {
    id,
    name: name || "New Group",
    to: "/components",
    visible: true,
    order: 0,
    isCustomGroup: true,
    children: [],
  };
  if (icon) group.icon = icon;
  return group;
}

/**
 * Remove an item from anywhere in the tree by ID.
 * Returns [newConfig, removedItem].
 */
export function removeItemFromTree(config, itemId) {
  let removed = null;

  const filter = (items) => {
    const result = [];
    for (const item of items) {
      if (item.id === itemId) {
        removed = { ...item };
        continue;
      }
      const newItem = { ...item };
      if (newItem.children && newItem.children.length > 0) {
        newItem.children = filter(newItem.children);
      }
      result.push(newItem);
    }
    return result;
  };

  const newConfig = filter(config);
  return [newConfig, removed];
}

/**
 * Add an item as a child of a target parent.
 * If targetParentId is null, adds to top level.
 */
export function addItemToParent(config, item, targetParentId) {
  if (!targetParentId) {
    // Add to top level
    return [...config, { ...item, order: config.length }];
  }

  return config.map((menuItem) => {
    if (menuItem.id === targetParentId) {
      const children = menuItem.children || [];
      return {
        ...menuItem,
        children: [...children, { ...item, order: children.length }],
      };
    }
    if (menuItem.children && menuItem.children.length > 0) {
      return {
        ...menuItem,
        children: addItemToParent(menuItem.children, item, targetParentId),
      };
    }
    return menuItem;
  });
}

/**
 * Move an item from its current position to a new parent.
 * If targetParentId is null, moves to top level.
 */
export function moveItem(config, itemId, targetParentId) {
  const [withoutItem, removed] = removeItemFromTree(config, itemId);
  if (!removed) return config;
  return addItemToParent(withoutItem, removed, targetParentId);
}

/**
 * Dissolve a group: remove the group and move its children to the group's
 * parent level (top level if the group is at top level).
 */
export function dissolveGroup(config, groupId) {
  const result = [];
  for (const item of config) {
    if (item.id === groupId) {
      // Move children to this level
      if (item.children && item.children.length > 0) {
        item.children.forEach((child) => {
          result.push({ ...child, order: result.length });
        });
      }
      continue; // skip the group itself
    }
    const newItem = { ...item };
    if (newItem.children && newItem.children.length > 0) {
      newItem.children = dissolveGroup(newItem.children, groupId);
    }
    result.push(newItem);
  }
  return result.map((item, idx) => ({ ...item, order: idx }));
}

/**
 * Get a flat list of all top-level items and groups (for "Move to" dropdown).
 * Returns [{id, name, icon}] including a "Top Level" option.
 */
export function getParentOptions(config, excludeId) {
  const options = [{ id: null, name: "-- Top Level --", icon: null }];
  config.forEach((item) => {
    if (item.id === excludeId) return;
    options.push({ id: item.id, name: item.name, icon: item.icon });
  });
  return options;
}

/**
 * Find which parent an item belongs to. Returns the parent ID or null if top-level.
 */
export function findParentId(config, itemId, currentParentId) {
  for (const item of config) {
    if (item.id === itemId) return currentParentId;
    if (item.children) {
      const found = findParentId(item.children, itemId, item.id);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}
